import * as THREE from 'three'
import { RAPIER } from '../physics'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { SlidingDoor } from '../world/doors'
import { DestructibleObject, DestructibleManager, type LootType } from '../world/destructibles'
import { ElevatorController } from '../world/elevator'
import { colliderRegistry } from '../world/collider-registry'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import propManifest from '../../assets/models/all_props_manifest.json'
import { getAuthenticMaterial } from './textures'

const MANIFEST_PROP_MAP = new Map(
  (propManifest as Array<any>).map((p) => [p.id, p]),
)
const MANIFEST_TYPE_MAP = new Map(
  (propManifest as Array<{ id: string; type?: string }>).map((p) => [p.id, p.type]),
)

export interface GltfLoadedProps {
  doors: SlidingDoor[]
  destructibles: DestructibleObject[]
  elevators: ElevatorController[]
  colliders: RAPIER.Collider[]
  meshes: THREE.Mesh[]
}

export class GltfPropLoader {
  private readonly scene: THREE.Scene
  private readonly world: RAPIER.World
  private readonly destructibleManager: DestructibleManager
  private readonly debugRegistry?: DebugMaterialRegistry
  private readonly gltfLoader: GLTFLoader

  constructor(
    scene: THREE.Scene,
    world: RAPIER.World,
    destructibleManager: DestructibleManager,
    debugRegistry?: DebugMaterialRegistry
  ) {
    this.scene = scene
    this.world = world
    this.destructibleManager = destructibleManager
    this.debugRegistry = debugRegistry
    this.gltfLoader = new GLTFLoader()
  }

  /**
   * Pre-fetch a unified bundle or concurrent set of individual prop geometries.
   * Returns a Map of prop identifier / file -> BufferGeometry.
   */
  async preloadGeometryBundle(
    bundleUrl: string,
    propFiles: string[] = [],
    baseUrl = '/assets/models/',
  ): Promise<Map<string, THREE.BufferGeometry>> {
    const geometryCache = new Map<string, THREE.BufferGeometry>()

    // 1. Fast-path: Load combined .glb bundle (single network request)
    try {
      const unifiedGlb = await this.gltfLoader.loadAsync(bundleUrl)
      unifiedGlb.scene.traverse((node) => {
        if (node instanceof THREE.Mesh && node.name) {
          geometryCache.set(`props/${node.name}.glb`, node.geometry)
          geometryCache.set(node.name, node.geometry)
        }
      })
    } catch {
      // Fallback to individual parallel fetches below
    }

    // 2. Concurrently fetch any missing files in parallel
    const missingFiles = Array.from(new Set(propFiles)).filter(
      (f) => !geometryCache.has(f) && !geometryCache.has(f.replace(/^props\//, '').replace(/\.glb$/, '')),
    )

    if (missingFiles.length > 0) {
      await Promise.all(
        missingFiles.map(async (file) => {
          try {
            const url = file.startsWith('/') || file.startsWith('http') ? file : `${baseUrl}${file}`
            const glb = await this.gltfLoader.loadAsync(url)
            let loadedMesh: THREE.Mesh | null = null
            glb.scene.traverse((node) => {
              if (!loadedMesh && node instanceof THREE.Mesh) loadedMesh = node
            })
            if (loadedMesh) {
              geometryCache.set(file, (loadedMesh as THREE.Mesh).geometry)
            }
          } catch {
            // Fallback geometry will be used by caller if needed
          }
        }),
      )
    }

    return geometryCache
  }

  /**
   * Load and process all glTF props for a specified sector
   */
  async loadSectorProps(sectorName: string, baseUrl = '/assets/models/'): Promise<GltfLoadedProps> {
    const cleanName = sectorName.replace('.e3', '').toLowerCase()
    const url = `${baseUrl}${cleanName}_props.glb`

    const timeoutMs = typeof window === 'undefined' ? 200 : 10000
    const loadPromise = this.gltfLoader.loadAsync(url)
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout loading ${url}`)), timeoutMs),
    )

    try {
      const gltf = await Promise.race([loadPromise, timeoutPromise])
      return this.processPropScene(gltf.scene)
    } catch (err: any) {
      throw new Error(`Missing or invalid glTF asset for sector '${sectorName}' at ${url}: ${err?.message || err}`)
    }
  }

  /**
   * Traverse a loaded glTF scene hierarchy, classify nodes, attach controllers & physics
   */
  processPropScene(root: THREE.Object3D): GltfLoadedProps {
    const doors: SlidingDoor[] = []
    const destructibles: DestructibleObject[] = []
    const elevators: ElevatorController[] = []
    const colliders: RAPIER.Collider[] = []
    const meshes: THREE.Mesh[] = []

    this.scene.add(root)
    root.updateMatrixWorld(true)

    root.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return

      const name = node.name || ''
      const tag = (node.userData?.tag as string) || name
      const type = (node.userData?.type as string) || ''

      // Apply authentic material and texture swatches from manifest
      const manifestEntry = MANIFEST_PROP_MAP.get(node.name)
      if (manifestEntry && manifestEntry.texture) {
        const mat = getAuthenticMaterial(manifestEntry.texture)
        node.material = mat
        if (!node.userData) node.userData = {}
        node.userData.currentTexture = manifestEntry.texture
        node.userData.swatches = manifestEntry.availableSwatches || [manifestEntry.texture]
      }

      // Authoritative type from manifest; undefined when mesh is not in manifest (e.g. BSP geometry)
      const manifestType = MANIFEST_TYPE_MAP.get(node.name)

      // 1. Sliding Doors (CSlidingDoor)
      if (
        manifestType === 'door' ||
        (manifestType === undefined &&
          (tag.includes('CSlidingDoor') || type === 'door' || name.startsWith('door_') || name.startsWith('CSlidingDoor')))
      ) {
        const door = this.createDoorFromNode(node, tag)
        doors.push(door)
        if (door.leftBody) {
          const col = door.leftBody.collider(0)
          if (col) colliders.push(col)
        }
        if (door.rightBody) {
          const col = door.rightBody.collider(0)
          if (col) colliders.push(col)
        }
        meshes.push(node)
      }
      // 2. Breakables / Crates (CBreakableObject)
      else if (
        manifestType === 'breakable' || manifestType === 'crate' || manifestType === 'destructible' ||
        (manifestType === undefined &&
          (tag.includes('CBreakableObject') || type === 'crate' || name.startsWith('crate_') || name.startsWith('CBreakableObject')))
      ) {
        const destructible = this.createDestructibleFromNode(node, tag)
        destructibles.push(destructible)
        this.destructibleManager.register(destructible)
        if (destructible.collider) {
          colliders.push(destructible.collider)
        }
        meshes.push(node)
      }
      // 3. Elevators (CElevator)
      else if (
        manifestType === 'elevator' ||
        (manifestType === undefined &&
          (tag.includes('CElevator') || type === 'elevator' || name.startsWith('elevator_') || name.startsWith('CElevator')))
      ) {
        const elevator = this.createElevatorFromNode(node, tag)
        elevators.push(elevator)
        if (elevator.body) {
          const col = elevator.body.collider(0)
          if (col) colliders.push(col)
        }
        meshes.push(node)
      }
      // Generic scenery prop mesh
      else {
        meshes.push(node)
        const col = this.createColliderForMesh(node)
        colliders.push(col)
      }
    })

    return {
      doors,
      destructibles,
      elevators,
      colliders,
      meshes,
    }
  }

  createColliderForMesh(mesh: THREE.Mesh, _shape: 'cuboid' | 'convexHull' = 'cuboid'): RAPIER.Collider {
    mesh.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(mesh)
    const size = new THREE.Vector3()
    bbox.getSize(size)
    const center = new THREE.Vector3()
    bbox.getCenter(center)

    const hx = Math.max(0.05, size.x / 2)
    const hy = Math.max(0.05, size.y / 2)
    const hz = Math.max(0.05, size.z / 2)

    const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(center.x, center.y, center.z)
    const body = this.world.createRigidBody(bodyDesc)

    const colDesc = RAPIER.ColliderDesc.cuboid(hx, hy, hz)
    const collider = this.world.createCollider(colDesc, body)

    const propCategory = 'destructible'
    const propDisplayName = `${mesh.name} (PROP)`

    colliderRegistry.register(collider.handle, {
      id: mesh.name || 'gltf_prop',
      name: propDisplayName,
      category: propCategory,
      sourceTag: mesh.userData?.tag || mesh.name,
    })

    const manifestEntry = MANIFEST_PROP_MAP.get(mesh.name)
    const dimM = [size.x, size.y, size.z] as [number, number, number]

    if (this.debugRegistry) {
      this.debugRegistry.registerMesh(mesh, 'destructible', mesh.material, {
        id: mesh.name || 'gltf_prop',
        name: propDisplayName,
        sourceTag: `ESOTERIA/AREAS.E3 : ${mesh.userData?.tag || mesh.name}`,
        dimensions: dimM,
        availableSwatches: manifestEntry?.availableSwatches,
        getDetails: () => {
          const currentTex = (mesh.userData?.currentTexture as string) || manifestEntry?.texture || 'airpwall1'
          const swatches = manifestEntry?.availableSwatches || []
          const tierStr = manifestEntry?.resolutionTier
            ? `Tier ${manifestEntry.resolutionTier} (${manifestEntry.resolutionRule || 'resolved'})`
            : 'Tier 3 (Archetype Default)'
          return {
            'ID': mesh.name,
            'Type': 'PROP',
            'Tag': mesh.userData?.tag || mesh.name,
            'Dimensions': `${dimM[0].toFixed(2)}m × ${dimM[1].toFixed(2)}m × ${dimM[2].toFixed(2)}m`,
            'Texture': `${currentTex}.png`,
            'Provenance': tierStr,
            'Swatches': swatches.length > 1 ? `[KeyT: cycle ${swatches.length} variants]` : '1 variant',
            'Source Verts': manifestEntry?.sourceVertexCount ?? 8,
            'Triangles': manifestEntry?.exportedTriangleCount ?? 12,
            'GLTF Asset': manifestEntry?.file ?? `props/${mesh.name}.glb`,
          }
        },
      })
    }

    return collider
  }

  private createDoorFromNode(node: THREE.Mesh, tag: string): SlidingDoor {
    node.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(node)
    const size = new THREE.Vector3()
    bbox.getSize(size)
    const center = new THREE.Vector3()
    bbox.getCenter(center)
    const manifestEntry = MANIFEST_PROP_MAP.get(node.name)

    // Determine door orientation axis and slide direction from authentic manifest data
    const slideAxis: 'x' | 'z' = (node.userData?.slideAxis as 'x' | 'z') ||
      (manifestEntry?.slideAxis as 'x' | 'z') ||
      (size.z > size.x ? 'z' : 'x')
    const slideDirection: 1 | -1 = (node.userData?.slideDirection as 1 | -1) ||
      (manifestEntry?.slideDirection as 1 | -1) ||
      -1
    const slideDistance = (node.userData?.slideDistance as number) || (slideAxis === 'x' ? size.x * 1.1 : size.z * 1.1)

    const door = new SlidingDoor({
      id: node.name || 'gltf_door',
      position: center,
      width: size.x,
      height: size.y,
      depth: size.z,
      slideAxis,
      slideDirection,
      slideDistance,
      openSpeed: 3.5,
    })

    door.leftMesh = node

    const bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(center.x, center.y, center.z)
    const body = this.world.createRigidBody(bodyDesc)

    const colDesc = RAPIER.ColliderDesc.cuboid(
      Math.max(0.05, size.x / 2),
      Math.max(0.05, size.y / 2),
      Math.max(0.05, size.z / 2)
    )
    const collider = this.world.createCollider(colDesc, body)

    door.leftBody = body

    colliderRegistry.register(collider.handle, {
      id: door.id,
      name: `Door (${door.id})`,
      category: 'door',
      sourceTag: tag,
    })

    const dimM = [size.x, size.y, size.z] as [number, number, number]

    if (this.debugRegistry) {
      this.debugRegistry.registerMesh(node, 'door', node.material, {
        id: door.id,
        name: `${door.id} (DOOR)`,
        sourceTag: `ESOTERIA/AREAS.E3 : ${tag}`,
        dimensions: dimM,
        availableSwatches: manifestEntry?.availableSwatches,
        getDetails: () => {
          const currentTex = (node.userData?.currentTexture as string) || manifestEntry?.texture || 'chkdoorsec_h'
          const swatches = manifestEntry?.availableSwatches || []
          const tierStr = manifestEntry?.resolutionTier
            ? `Tier ${manifestEntry.resolutionTier} (${manifestEntry.resolutionRule || 'resolved'})`
            : 'Tier 3 (Archetype Default)'
          return {
            'ID': door.id,
            'Type': 'DOOR',
            'Tag': tag,
            'State': door.state.toUpperCase(),
            'Open Progress': `${(door.openProgress * 100).toFixed(0)}%`,
            'Dimensions': `${dimM[0].toFixed(2)}m × ${dimM[1].toFixed(2)}m × ${dimM[2].toFixed(2)}m`,
            'Texture': `${currentTex}.png`,
            'Provenance': tierStr,
            'Swatches': swatches.length > 1 ? `[KeyT: cycle ${swatches.length} variants]` : '1 variant',
            'Source Verts': manifestEntry?.sourceVertexCount ?? 8,
            'Triangles': manifestEntry?.exportedTriangleCount ?? 12,
            'GLTF Asset': manifestEntry?.file ?? `props/${door.id}.glb`,
          }
        },
      })
    }

    return door
  }

  private createDestructibleFromNode(node: THREE.Mesh, tag: string): DestructibleObject {
    node.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(node)
    const size = new THREE.Vector3()
    bbox.getSize(size)
    const center = new THREE.Vector3()
    bbox.getCenter(center)

    const health = (node.userData?.health as number) || 40
    const lootDrop = (node.userData?.lootType as LootType) || 'health-tank'

    const destructible = new DestructibleObject({
      id: node.name || 'gltf_crate',
      position: center,
      size: [size.x, size.y, size.z],
      maxHp: health,
      lootDrop,
    })

    destructible.mesh = node

    const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(center.x, center.y, center.z)
    const body = this.world.createRigidBody(bodyDesc)

    const colDesc = RAPIER.ColliderDesc.cuboid(
      Math.max(0.05, size.x / 2),
      Math.max(0.05, size.y / 2),
      Math.max(0.05, size.z / 2)
    )
    const collider = this.world.createCollider(colDesc, body)

    destructible.collider = collider
    destructible.body = body

    colliderRegistry.register(collider.handle, {
      id: destructible.id,
      name: `Destructible (${destructible.id})`,
      category: 'destructible',
      sourceTag: tag,
    })

    const manifestEntry = MANIFEST_PROP_MAP.get(node.name)
    const dimM = [size.x, size.y, size.z] as [number, number, number]

    if (this.debugRegistry) {
      this.debugRegistry.registerMesh(node, 'destructible', node.material, {
        id: destructible.id,
        name: `${destructible.id} (DESTRUCTIBLE)`,
        sourceTag: `ESOTERIA/AREAS.E3 : ${tag}`,
        dimensions: dimM,
        availableSwatches: manifestEntry?.availableSwatches,
        getDetails: () => {
          const currentTex = (node.userData?.currentTexture as string) || manifestEntry?.texture || 'chkcrate-f_h'
          const swatches = manifestEntry?.availableSwatches || []
          const tierStr = manifestEntry?.resolutionTier
            ? `Tier ${manifestEntry.resolutionTier} (${manifestEntry.resolutionRule || 'resolved'})`
            : 'Tier 3 (Archetype Default)'
          return {
            'ID': destructible.id,
            'Type': 'DESTRUCTIBLE',
            'Tag': tag,
            'HP': `${destructible.hp}/${destructible.maxHp}`,
            'Loot Drop': lootDrop,
            'Dimensions': `${dimM[0].toFixed(2)}m × ${dimM[1].toFixed(2)}m × ${dimM[2].toFixed(2)}m`,
            'Texture': `${currentTex}.png`,
            'Provenance': tierStr,
            'Swatches': swatches.length > 1 ? `[KeyT: cycle ${swatches.length} variants]` : '1 variant',
            'Source Verts': manifestEntry?.sourceVertexCount ?? 8,
            'Triangles': manifestEntry?.exportedTriangleCount ?? 12,
            'GLTF Asset': manifestEntry?.file ?? `props/${destructible.id}.glb`,
          }
        },
      })
    }

    return destructible
  }

  private createElevatorFromNode(node: THREE.Mesh, tag: string): ElevatorController {
    node.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(node)
    const size = new THREE.Vector3()
    bbox.getSize(size)
    const center = new THREE.Vector3()
    bbox.getCenter(center)

    const bottomY = typeof node.userData?.bottomY === 'number' ? node.userData.bottomY : center.y
    const topY = typeof node.userData?.topY === 'number' ? node.userData.topY : bottomY + 5.0
    const travelDistance = Math.max(0.5, topY - bottomY)

    const elevator = new ElevatorController({
      id: node.name || 'gltf_elevator',
      position: new THREE.Vector3(center.x, bottomY, center.z),
      size: [size.x, size.y, size.z],
      travelDistance,
      speed: (node.userData?.speed as number) || 2.0,
      waitDelay: (node.userData?.waitDelay as number) || 2.0,
    })

    elevator.mesh = node

    const bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(center.x, bottomY, center.z)
    const body = this.world.createRigidBody(bodyDesc)

    const colDesc = RAPIER.ColliderDesc.cuboid(
      Math.max(0.05, size.x / 2),
      Math.max(0.05, size.y / 2),
      Math.max(0.05, size.z / 2)
    )
    const collider = this.world.createCollider(colDesc, body)

    elevator.body = body

    colliderRegistry.register(collider.handle, {
      id: elevator.id,
      name: `Elevator (${elevator.id})`,
      category: 'elevator',
      sourceTag: tag,
    })

    const manifestEntry = MANIFEST_PROP_MAP.get(node.name)
    const dimM = [size.x, size.y, size.z] as [number, number, number]

    if (this.debugRegistry) {
      this.debugRegistry.registerMesh(node, 'elevator', node.material, {
        id: elevator.id,
        name: `${elevator.id} (ELEVATOR)`,
        sourceTag: `ESOTERIA/AREAS.E3 : ${tag}`,
        dimensions: dimM,
        availableSwatches: manifestEntry?.availableSwatches,
        getDetails: () => {
          const currentTex = (node.userData?.currentTexture as string) || manifestEntry?.texture || 'baseelev1'
          const swatches = manifestEntry?.availableSwatches || []
          const tierStr = manifestEntry?.resolutionTier
            ? `Tier ${manifestEntry.resolutionTier} (${manifestEntry.resolutionRule || 'resolved'})`
            : 'Tier 3 (Archetype Default)'
          return {
            'ID': elevator.id,
            'Type': 'ELEVATOR',
            'Tag': tag,
            'State': elevator.state.toUpperCase(),
            'Height': `${elevator.position.y.toFixed(2)}m`,
            'Dimensions': `${dimM[0].toFixed(2)}m × ${dimM[1].toFixed(2)}m × ${dimM[2].toFixed(2)}m`,
            'Texture': `${currentTex}.png`,
            'Provenance': tierStr,
            'Swatches': swatches.length > 1 ? `[KeyT: cycle ${swatches.length} variants]` : '1 variant',
            'Source Verts': manifestEntry?.sourceVertexCount ?? 8,
            'Triangles': manifestEntry?.exportedTriangleCount ?? 12,
            'GLTF Asset': manifestEntry?.file ?? `props/${elevator.id}.glb`,
          }
        },
      })
    }

    return elevator
  }
}
